import { useEffect } from "react";
import { useRouter } from "@tanstack/react-router";

import { cookieConsentConfig } from "@/lib/cookie-consent";
import { useLocale } from "@/lib/i18n";
import { loadMetaPixel, trackPageView } from "@/lib/meta-pixel";

type Props = {
  /** Meta Pixel ID from the runtime env; null disables the pixel and the banner. */
  pixelId: string | null;
};

/**
 * Cookie consent (vanilla-cookieconsent) gating the Meta Pixel: nothing is
 * loaded from Meta until the visitor accepts the marketing category.
 */
export function CookieConsent({ pixelId }: Props) {
  const locale = useLocale();
  const router = useRouter();

  useEffect(() => {
    if (!pixelId) return;
    // Client-only library: imported lazily so it never runs during SSR.
    import("vanilla-cookieconsent").then((cc) =>
      cc.run(cookieConsentConfig(locale, () => loadMetaPixel(pixelId))),
    );
    return router.subscribe("onResolved", ({ fromLocation, toLocation }) => {
      if (fromLocation && fromLocation.pathname !== toLocation.pathname) trackPageView();
    });
    // `run` is one-shot; locale switches are handled below via setLanguage.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pixelId, router]);

  useEffect(() => {
    if (!pixelId) return;
    import("vanilla-cookieconsent").then((cc) => cc.setLanguage(locale));
  }, [pixelId, locale]);

  return null;
}
